import React, { FC, useEffect, useState } from 'react';
import { useI18n, useLogger, useSelector } from '~Hooks';
import BackTopBar from '~ComponentsImproved/BackTopBar';
import styled from '@emotion/styled';
import { Box } from '~Styles';
import { useTableUniverse } from '~Components/TableUniverse';
import { Cheque } from '@baemingo/table-events/dist/types';
import ParkedOrderRow from './Components/ParkedOrderRow';
import ParkedOrderDetails from './Components/ParkedOrderDetails';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { DateTime } from 'luxon';
import { PARKED_ORDER_TABLE_ID } from './consts';

const Wrapper = styled(Box)`
  flex: 1;
  background-color: var(--colorBlueGray1200);
  color: var(--colorWhite);
  height: calc(100vh - 64px);
  position: fixed;
  z-index: 99;
  width: 100%;
  height: 100%;
`;

const ScrollWrapper = styled(Box)`
  overflow-y: auto;
  height: 100%;
  flex: 1;
  padding: 20px 40px 0px 20px;
  box-sizing: border-box;
  opacity: 0;
  animation: fadeIn 0.2s ease-in-out 0.1s forwards;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ParkedOrdersWrapper = styled(Box)`
  flex: 1;
  overflow-y: auto;
  padding-bottom: 20px;

  position: relative;

  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  mask-image: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 1) calc(100% - 20px),
    rgba(0, 0, 0, 0) 100%
  );
  -webkit-mask-image: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 1) calc(100% - 20px),
    rgba(0, 0, 0, 0) 100%
  );

  background-image: linear-gradient(
    to bottom,
    transparent calc(100% - 20px),
    var(--colorBlueGray1200) 100%
  );
`;

/** Parent component for the ParkedOrders scene */
const ParkedOrders: FC = () => {
  const { i18n } = useI18n();

  const tableUniverse = useTableUniverse();

  /** Get parked orders - When parking an order we create a cheque with PARKED_ORDER_TABLE_ID from ./consts */
  const parkedOrders = tableUniverse.cheques
    .filter((cheque) => cheque.tableId === PARKED_ORDER_TABLE_ID && cheque.name)
    .sort(
      (a, b) =>
        DateTime.fromISO(b.createdAt).toMillis() - DateTime.fromISO(a.createdAt).toMillis(),
    );

  const [selectedParkedOrder, setSelectedParkedOrder] = useState<Cheque | null>(null);

  /** Init selectedOrder */
  useEffect(() => {
    if (parkedOrders.length === 0) {
      setSelectedParkedOrder(null);
      return;
    }

    if (selectedParkedOrder) {
      return;
    }

    selectFirstParkedOrder();
  }, [selectedParkedOrder, parkedOrders.length]);

  const selectFirstParkedOrder = (removedId?: string) => {
    let orders = parkedOrders;
    if (removedId) {
      orders = orders.filter((order) => order.id !== removedId);
    }

    setSelectedParkedOrder(orders[0]);
  };

  return (
    <Wrapper display="flex" flexDirection="column">
      <BackTopBar title={i18n('ParkedOrders.Title')} dark />

      {selectedParkedOrder ? (
        <ScrollWrapper
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          gap="large">
          <ParkedOrdersWrapper display="flex" flexDirection="column" gap="small">
            {parkedOrders.map((parkedOrder, idx: number) => (
              <ParkedOrderRow
                key={parkedOrder.id}
                order={parkedOrder}
                isSelected={selectedParkedOrder?.id === parkedOrder.id}
                onSelectOrder={(o) => setSelectedParkedOrder(o)}
              />
            ))}
          </ParkedOrdersWrapper>

          <ParkedOrderDetails
            selectedParkedOrder={selectedParkedOrder}
            onSelectedParkedOrderChange={setSelectedParkedOrder}
            selectFirstParkedOrder={selectFirstParkedOrder}
          />
        </ScrollWrapper>
      ) : (
        <ScrollWrapper>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            padding="xl"
            textAlign="center">
            <Heading level={1} color="var(--colorWhite)" bottom={24}>
              {i18n('ParkedOrders.NoParkedOrders')}
            </Heading>
            <Paragraph level={2} color="var(--colorBlueGray200)">
              {i18n('ParkedOrders.NoParkedOrders.Desc')}
            </Paragraph>
          </Box>
        </ScrollWrapper>
      )}
    </Wrapper>
  );
};

export default ParkedOrders;
